import { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { GlobalContext } from "../GlobalContext";
import Error from "../../pages/Error";
import {
  TextField,
  NumberField,
  BooleanField,
  DateField,
  TimeField,
  ImageField,
  FileField,
  SingleReferenceField,
  MultipleReferenceField,
} from "./Fields";
import {
  Button,
  HStack,
  VStack,
  Spacer,
  Box,
  Card,
  Heading,
  CardBody,
  Center,
  Tooltip,
  IconButton,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import theme from "../../theme";
import { FontAwesomeIcon as FA } from "@fortawesome/react-fontawesome";
import { faArrowRightRotate, faChartColumn, faMagnifyingGlass, faUpload, faX } from "@fortawesome/free-solid-svg-icons";
import { normalizePropName } from "../../utils/normalization";

const EditForm = () => {
  const { userInfo, settings, setSettings, runCode, fqlToken, loading, setLoading } =
    useContext(GlobalContext);
  const { form_name } = useParams();
  const [formData, setFormData] = useState([]);
  const [error, setError] = useState(false);
  const [resetDisabled, setResetDisabled] = useState(false);

  const getInfo = async () => {
    await runCode({
      code: `show forms ${form_name}`,
      token: userInfo.token,
    })
      .then((data) => {
        if (data.output && data.output.length !== 0)
          setFormData(data.output[0]["DATA-SPECS"]);
        else {
          setError(true);
          setTimeout(() => {
            setLoading(false);
          }, 500);
        }
        setTimeout(() => {
          setLoading(false);
        }, 500);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false)
      });
  };

  useEffect(() => {
    setLoading(true)
    if (userInfo && userInfo.token) {
      getInfo();
    } else {
      setError(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [form_name]);

  useEffect(() => {
    if (!loading) {
      const inputFields = document.querySelectorAll('.input-ps-cn');

      const checkInputs = () => {
        const hasValue = Array.from(inputFields).some(field => field.value !== '');
        setResetDisabled(!hasValue);
      };

      inputFields?.forEach(field => {
        if (field.tagName === 'select')
          field.addEventListener('selectionchange', checkInputs);
        else
          field.addEventListener('input', checkInputs);
      });

      checkInputs();
    }
  }, [loading, form_name])

  const pushValue = (codeDict, title, value) => {
    if (codeDict[title]) {
      codeDict[title].push(value);
    } else {
      codeDict[title] = [value];
    }
  };

  const handleClearFields = () => {
    const inputs = document.getElementsByClassName("input-ps-cn");

    for (let i = 0; i < inputs.length; i++) {
      inputs[i].value = "";
      if (inputs[i].type === "file") {
        const index = inputs[i].id.split("-")[1];
        const clearElement = document.getElementById(`input-clear-${index}`);
        clearElement.click();
      }
    }
    setResetDisabled(true);
  };

  const handleCreateNew = async () => {
    const inputs = document.getElementsByClassName("input-ps-cn");
    const codeDict = {};

    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      if (input.required) {
        if (input.value === "" || !input.value || input.value === null) {
          const requiredText = document.getElementById("required-text");
          requiredText.innerHTML = "* This field is required";
          setTimeout(() => {
            requiredText.innerHTML = "";
          }, 3000);
          return;
        }
      }

      if (!(input.value === "" || !input.value || input.value === null)) {
        switch (input.attributes.datatype.value) {
          case "text":
            pushValue(codeDict, input.title, `'${input.value}'`);
            break;
          case "date":
            pushValue(codeDict, input.title, `'${input.value}'`);
            break;
          case "number":
            pushValue(codeDict, input.title, input.value);
            break;
          case "boolean":
            pushValue(codeDict, input.title, input.value);
            break;
          case "file":
            if (input.textvalue === "" || input.textvalue === undefined) {
              pushValue(codeDict, input.title, `'${JSON.stringify({})}'`);
              break;
            }
            pushValue(codeDict, input.title, `'${input.textvalue}'`);
            break;
          case "image":
            if (input.textvalue === "" || input.textvalue === undefined) {
              pushValue(codeDict, input.title, `'${JSON.stringify({})}'`);
              break;
            }
            pushValue(codeDict, input.title, `'${input.textvalue}'`);
            break;
          default:
            break;
        }
      }
    }

    const keys = Object.keys(codeDict);
    const codeArray = [];

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = codeDict[key];

      if (value.length > 1) {
        codeArray.push(`${key} = (${value.join(", ")})`);
      } else {
        codeArray.push(`${key} = ${value[0]}`);
      }
    }

    const code = `create new ${form_name}(${codeArray.join(", ")})`;
    console.log(code);

    setLoading(true);
    setSettings({
      ...settings,
      message: { ...settings.message, show: false },
    });

    runCode({ code: code, token: userInfo.token })
      .then((data) => {
        setSettings({
          ...settings,
          message: {
            ...settings.message,
            status: data.msg.split(" ")[0].toLowerCase(),
            show: true,
            data: data.msg,
            fn: handleClearFields,
          },
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setSettings({
          ...settings,
          message: {
            ...settings.message,
            status: "error",
            show: true,
            data: err.message,
            fn: () => { },
          },
        });
      });
  };

  const getField = (data, index) => {
    switch (data.TYPE) {
      case "TEXT":
        return <TextField data={data} index={index} />;
      case "NUMBER":
        return <NumberField data={data} index={index} />;
      case "BOOLEAN":
        return <BooleanField data={data} index={index} />;
      case "DATE":
        return <DateField data={data} index={index} />;
      case "TIME":
        return <TimeField data={data} index={index} />;
      case "IMAGE":
        return <ImageField data={data} index={index} />;
      case "FILE":
        return <FileField data={data} index={index} />;
      case "REFERENCE":
        const minCard = data["REF-CARDINALITY"][0];
        const maxCard = data["REF-CARDINALITY"][1];

        if (maxCard - minCard > 1)
          return (
            <MultipleReferenceField
              data={data}
              index={index}
              minCard={minCard}
              maxCard={maxCard}
            />
          );
        else
          return <SingleReferenceField data={data} index={index} parent="" />;

      default:
        break;
    }
  };

  const handleFillFields = (data) => {
    const inputs = document.getElementsByClassName("input-ps-cn");
    const refDict = {};
    
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      setResetDisabled(false)
      if (input.type === "file") {
        input.textvalue = data[input.title.toLowerCase()];
        const index = input.id.split("-")[1];
        const fillElement = document.getElementById(`input-fill-${index}`);
        fillElement.click();
        continue;
      } else if (
        input.parentElement.attributes.reftype &&
        input.parentElement.attributes.reftype.value === "ref-mult"
      ) {
        if (!refDict[input.title]) {
          refDict[input.title] = "done";
          const index = input.id.split("-")[1];
          const fillElement = document.getElementById(`input-fill-${index}`);
          fillElement.attributes.data.value = JSON.stringify(data);
          fillElement.click();
        }
      } else input.value = data[input.title.toLowerCase()];
    }
  };

  const handleGetEntries = async () => {
    const inputs = document.getElementsByClassName("input-ps-cn");
    const code = [];

    for (let i = 0; i < inputs.length; i++) {
      const item = inputs[i];
      if (item.value && item.value !== "") {
        if (code.length === 0) {
          code.push(`with ${item.title} = '${item.value}' `);
        } else {
          code.push(`and ${item.title} = '${item.value}' `);
        }
      }
    }
    setLoading(true);
    setSettings({
      ...settings,
      message: { ...settings.message, show: false },
    });
    fqlToken.current = undefined;

    await runCode({
      code: `get ${form_name} ${code.join("")}`,
      token: userInfo.token,
    })
      .then((data) => {
        if (data.output && data.output.length > 0) {
          if (data.output.length === 1) {
            handleFillFields(data.output[0]);
            fqlToken.current = data.fql_token;
          } else {
            setSettings({
              ...settings,
              modals: {
                ...settings.modals,
                title: "SEARCH RESULTS",
                icon: "icon-fields-form",
                id: "modal-get",
                show: true,
                data: data.output,
              },
              resetButtons: setResetDisabled
            });
            fqlToken.current = data.fql_token;
          }
          setLoading(false);
        } else {
          setSettings({
            ...settings,
            message: {
              ...settings.message,
              status: "info",
              show: true,
              data: data.msg,
              fn: () => { },
            },
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setSettings({
          ...settings,
          message: {
            ...settings.message,
            status: "error",
            show: true,
            data: err.message.toUpperCase(),
            fn: () => { },
          },
        });
        setLoading(false);
      });
  };

  const handleModifyCase = async () => {
    const inputs = document.getElementsByClassName("input-ps-cn");
    const code = [];

    for (let i = 0; i < inputs.length; i++) {
      const item = inputs[i];
      if (item.value) {
        code.push(`'${item.value}'`);
      }
    }
    setLoading(true);
    setSettings({
      ...settings,
      message: { ...settings.message, show: false },
    });
    const command = `modify ${form_name} (${code.join(", ")})`;

    console.log(command)

    await runCode({
      code: command,
      token: userInfo.token,
    })
      .then((data) => {
        if (data.msg) {
          const status = data.msg.split(" ")[0].toLowerCase();
          setSettings({
            ...settings,
            message: {
              ...settings.message,
              status: status,
              show: true,
              data: data.msg,
              fn: status !== "error" ? handleClearFields : () => { },
            },
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setSettings({
          ...settings,
          message: {
            ...settings.message,
            status: "error",
            show: true,
            data: err.message.toUpperCase(),
            fn: () => { },
          },
        });
        setLoading(false);
      });
  };

  const handleRemoveCase = async () => {
    const inputs = document.getElementsByClassName("input-ps-cn");
    const codeDict = {};

    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];

      if (!(input.value === "" || !input.value || input.value === null)) {
        switch (input.attributes.datatype.value) {
          case "text":
            pushValue(codeDict, input.title, `'${input.value}'`);
            break;
          case "date":
            pushValue(codeDict, input.title, `'${input.value}'`);
            break;
          case "number":
            pushValue(codeDict, input.title, input.value);
            break;
          case "boolean":
            pushValue(codeDict, input.title, input.value);
            break;
          case "file":
            if (input.textvalue === "" || input.textvalue === undefined) {
              pushValue(codeDict, input.title, `'${JSON.stringify({})}'`);
              break;
            }
            pushValue(codeDict, input.title, `'${input.textvalue}'`);
            break;
          case "image":
            if (input.textvalue === "" || input.textvalue === undefined) {
              pushValue(codeDict, input.title, `'${JSON.stringify({})}'`);
              break;
            }
            pushValue(codeDict, input.title, `'${input.textvalue}'`);
            break;
          default:
            break;
        }
      }
    }

    const keys = Object.keys(codeDict);
    const codeArray = [];

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = codeDict[key];

      if (value.length > 1) {
        codeArray.push(`${key} = (${value.join(", ")})`);
      } else {
        codeArray.push(`${key} = ${value[0]}`);
      }
    }

    const code = `remove ${form_name}`;

    console.log(code)

    setLoading(true);
    setSettings({
      ...settings,
      message: { ...settings.message, show: false },
    });

    runCode({
      code: code,
      token: userInfo.token,
    })
      .then((data) => {
        setSettings({
          ...settings,
          message: {
            ...settings.message,
            status: data.msg.split(" ")[0].toLowerCase(),
            show: true,
            data: data.msg,
            fn: handleClearFields,
          },
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setSettings({
          ...settings,
          message: {
            ...settings.message,
            status: "error",
            show: true,
            data: err.message,
            fn: () => { },
          },
        });
      });
  };

  const faDottedSquare = <svg width="30" height="30" color="white" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="12" width="40" height="40" stroke="white" strokeWidth="5" strokeDasharray="8 8" fill="none" />
  </svg>;

  return (
    <Box fontFamily={theme.fonts.body}>
      {!error ? (
        <>
          <div className="container">
            <div className={"items-container row"}>
              <VStack mt={10} mb={5} spacing={4}>
                <Heading size="xl" zIndex="999">
                  {normalizePropName(form_name)}
                </Heading>

                <HStack spacing={3} pb={5}>
                  <Tooltip label="Search" hasArrow>
                    <IconButton
                      onClick={handleGetEntries}
                      aria-label="Search"
                      icon={<FA icon={faMagnifyingGlass} />}
                      size={"lg"}
                    />
                  </Tooltip>

                  <Tooltip label="Modify" hasArrow>
                    <IconButton
                      onClick={handleModifyCase}
                      aria-label="Modify"
                      icon={<FA icon={faArrowRightRotate} />}
                      size={"lg"}
                    />

                  </Tooltip>

                  <Tooltip label="Delete" hasArrow>
                    <IconButton
                      aria-label="Delete"
                      icon={<FA icon={faX} />}
                      size={"lg"}
                      _hover={resetDisabled ? {} : { bg: "brand.400" }}
                      transition={"ease-in-out 0.25s"}
                      disabled={resetDisabled}
                      onClick={handleRemoveCase}
                    />
                  </Tooltip>

                  <Tooltip label="Show Chart" hasArrow>
                    <IconButton
                      aria-label="Show Chart"
                      icon={<FA icon={faChartColumn} />}
                      size={"lg"}
                      _hover={{}}
                      disabled
                    />
                  </Tooltip>

                  <Tooltip label="Upload File(s)" hasArrow>
                    <IconButton
                      aria-label="Upload File(s)"
                      icon={<FA icon={faUpload} />}
                      size={"lg"}
                      _hover={{}}
                      disabled
                    />
                  </Tooltip>

                  <Tooltip label="Reset fields" hasArrow>
                    <IconButton
                      aria-label="Reset fields"
                      onClick={handleClearFields}
                      icon={faDottedSquare}
                      size={"lg"}
                      disabled={resetDisabled}
                      _hover={resetDisabled ? {} : { bg: "brand.400" }}
                      transition={"ease-in-out 0.25s"}
                    />

                  </Tooltip>
                </HStack>
              </VStack>
              <Center>
                <VStack minW="650px" mb={20}>
                  <Card w="100%" py={5} px={8} mb={8}>
                    <CardBody>
                      {loading ?
                        <Center>
                          <Spinner
                            thickness="4px"
                            speed="0.65s"
                            emptyColor="gray.200"
                            color="brand.500"
                            size="lg"
                          />
                        </Center>
                        : formData.map((item, index) => (
                          <div
                            key={`div-ps-cn-${index}`}
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            {getField(item, index)}
                          </div>
                        ))
                      }
                    </CardBody>
                  </Card>
                  <HStack w="100%" px={2} gap={5}>
                    <Button
                      variant="link"
                      as={RouterLink}
                      to="/old/presentation_server/home"
                    >
                      <FA icon="arrow-left" />
                      Back
                    </Button>
                    <Spacer />
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={handleClearFields}
                      disabled={resetDisabled}
                      _hover={{}}
                    >
                      Reset
                    </Button>
                    <Button onClick={handleCreateNew} size="md">
                      Create
                    </Button>
                  </HStack>
                </VStack>
              </Center>
            </div>
          </div>
        </>
      ) : (
        <Error />
      )}
    </Box>
  );
};

export default EditForm;
